/**
 * The one caching layer for server state (coding standards 11.10).
 *
 * This sits on top of `client.ts` and does not replace it: every URL is still
 * written down in exactly one place. What is added here is when a call is
 * made and what its answer is filed under. The first UI is read only (spec
 * D4), so there is no mutation and nothing to invalidate yet.
 */
import { QueryClient, useQuery } from "@tanstack/react-query";

import { api } from "./client";

/**
 * The client, with two defaults changed and a reason for each.
 *
 * `retry: false`, because a failure here is information. A 403 is a person
 * without the dispatcher role and a 404 is a run or a day that does not exist;
 * three silent retries turn either into a spinner that says the same thing
 * much later. The 401 path runs through a failure too: the client asks the
 * session to sign in, and retrying a call that cannot be authenticated asks
 * three times.
 *
 * `refetchOnWindowFocus: false`, because a plan that silently changes under a
 * dispatcher who tabbed away and back is a different plan wearing the same
 * address.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: false, refetchOnWindowFocus: false },
      mutations: { retry: false },
    },
  });
}

/** What each answer is filed under. Everything starts with `runs`, the
 *  endpoints' own first word, so one invalidation reaches all of it the day
 *  something writes. */
export const queryKeys = {
  runs: () => ["runs"] as const,
  day: (runId: number, date: string) => ["runs", runId, "days", date] as const,
  findings: (runId: number, from: string, to: string) =>
    ["runs", runId, "findings", from, to] as const,
  week: (runId: number, monday: string) => ["runs", runId, "weeks", monday] as const,
};

export function useRuns() {
  return useQuery({ queryKey: queryKeys.runs(), queryFn: () => api.runs() });
}

/** `enabled` is false for an address that names no day at all, which is a
 *  call that can only be refused. */
export function useDay(runId: number, date: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.day(runId, date),
    queryFn: () => api.day(runId, date),
    enabled,
  });
}

export function useFindings(runId: number, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.findings(runId, from, to),
    queryFn: () => api.findings(runId, from, to),
    enabled,
  });
}

export function useWeek(runId: number, monday: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.week(runId, monday),
    queryFn: () => api.week(runId, monday),
    enabled,
  });
}
